import type { MouseEvent, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useClickOutside from '@/hooks/useClickOutside';

export type MouseFollowTooltipPoint = { x: number; y: number };

export type MouseFollowTooltipContainerProps = {
  ref: RefObject<HTMLDivElement>;
  onMouseMove: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
  onMouseOver: (event: MouseEvent<HTMLDivElement>) => void;
};

/** Tracks hover state and pointer position for a tooltip that follows the cursor inside the container. */
export const useMouseFollowTooltip = (enabled: boolean): {
  containerRef: RefObject<HTMLDivElement>;
  isOpen: boolean;
  point: MouseFollowTooltipPoint;
  containerProps: MouseFollowTooltipContainerProps;
} => {
  const [isOpen, setIsOpen] = useState(false);
  const [point, setPoint] = useState<MouseFollowTooltipPoint>({ x: 0, y: 0 });
  const isOpenRef = useRef(false);

  const containerRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false), {
    excludeSelector: 'a',
  });

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!enabled) setIsOpen(false);
  }, [enabled]);

  const onMouseOver = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!enabled) return;
      setIsOpen(true);
      setPoint({ x: event.clientX, y: event.clientY });
    },
    [enabled]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!enabled || !isOpenRef.current) return;
      setPoint({ x: event.clientX, y: event.clientY });
    },
    [enabled]
  );

  const onMouseLeave = useCallback(() => {
    if (!enabled) return;
    setIsOpen(false);
  }, [enabled]);

  const containerProps = useMemo<MouseFollowTooltipContainerProps>(
    () => ({
      ref: containerRef,
      onMouseLeave,
      onMouseMove,
      onMouseOver,
    }),
    [containerRef, onMouseLeave, onMouseMove, onMouseOver]
  );

  return { containerRef, isOpen, point, containerProps };
};
